import { Injectable } from "@angular/core";
import {
  ENTITY_TYPE,
  IDrugInfo,
  IDrugIntakeNotif,
  IDrugStock,
  IDrugStockIncrement,
  IDrugsHistoric,
  IDrugsInfo,
  IEntitylink,
  IExportDrugIntakePdfBody,
  NOTIFICATION_TYPE,
  Notification,
} from "@comunicare/comunicare-interfaces";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "./api.service";
import { BaseApiService } from "./baseApi.service";

@Injectable({
  providedIn: "root",
})
export class DrugsApiService extends BaseApiService {
  constructor(private api: ApiService) {
    super(
      "Drugs", // Theme !
      ["entity", "dashboard/drugIntake", "dashboard/exportDrugIntakePdf"], // Post route paths
      [
        "entitylinks", // 0
        "notifications", // 1
        "drugsInfo", // 2
        "dashboard/entitylink", // 3
        "dashboard/drugsHistoric", // 4
        "dashboard/exportDrugsForPatient", // 5
        "drugInfo", // 6
      ], // Get route paths
      ["dashboard/drugStock", "dashboard/drugIntake"], // Update route paths
      [] // Delete route paths
    );
  }

  public list(patientId: string): Observable<IEntitylink[]> {
    return this.api.get(this.readRoutes[0], {
      patientid: patientId,
      ENTITY_TYPE: ENTITY_TYPE.DRUG,
    }) as Observable<IEntitylink[]>;
  }

  public listIntake(patientId: string, itemId?: string): Observable<Notification[]> {
    return this.api.get(this.readRoutes[1], {
      account: patientId,
      type: NOTIFICATION_TYPE.DRUG,
      itemId,
    }) as Observable<Notification[]>;
  }

  public listInfo(query: string, lang: string): Observable<IDrugInfo[]> {
    return this.api.get(this.readRoutes[2], { name: query, lang: lang }).pipe(map((v: IDrugsInfo) => v.results));
  }

  public getOne(id: string): Observable<IEntitylink> {
    return this.api.get(this.readRoutes[3], { id }) as Observable<IEntitylink>;
  }

  public getHistoric(patientId: string): Observable<IDrugsHistoric[]> {
    return this.api.get(this.readRoutes[4], { patientId }) as Observable<IDrugsHistoric[]>;
  }

  public save(drug: IEntitylink): Observable<number> {
    return this.api.post(this.createRoutes[0], drug) as Observable<number>;
  }

  public exportDrugs(patientId: string, lang: string): Observable<Blob> {
    return this.api.getBlob(this.readRoutes[5], { patientId: patientId, lang: lang }) as Observable<Blob>;
  }

  public createDrugIntakes(drugIntakeNotif: IDrugIntakeNotif[]): Observable<Notification> {
    return this.api.post(this.createRoutes[1], drugIntakeNotif) as Observable<Notification>;
  }

  public getDrugInfo(reference: string, source: string): Observable<IDrugInfo> {
    return this.api.get(this.readRoutes[6], { cnk: reference, source }) as Observable<IDrugInfo>;
  }

  public getDrugStock(patientId: string, drugId: string): Observable<IDrugStock> {
    return this.api.get("dashboard/drugStock", { patientId, drugId }) as Observable<IDrugStock>;
  }

  public updateDrugStock(stockUpdate: IDrugStockIncrement): Observable<IDrugStockIncrement> {
    return this.api.put(this.updateRoutes[0], stockUpdate) as Observable<IDrugStockIncrement>;
  }

  public createDrugIntakesPDF(drugIntakePdfBody: IExportDrugIntakePdfBody): Observable<Blob> {
    return this.api.postReturnBlob(this.createRoutes[2], drugIntakePdfBody) as Observable<Blob>;
  }

  public updateDrugIntake(drugIntakeNotif: IDrugIntakeNotif): Observable<Notification> {
    return this.api.put(this.updateRoutes[1], drugIntakeNotif) as Observable<Notification>;
  }
}
